import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowLeft, House, MapPin, Envelope, ShareNetwork, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegistrationSuccess() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useTranslation();

  const ref = params.get("ref");
  const initialFromState = location.state || null;
  const [info, setInfo] = useState(initialFromState);
  const [loading, setLoading] = useState(!!ref && !initialFromState);

  // Si venimos desde MercadoPago (?ref=...), traer datos del backend.
  useEffect(() => {
    if (!ref || info) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/event-registrations/${ref}/confirmation`);
        setInfo(data);
      } catch (err) {
        // No mostrar error crudo: mostrar mensaje genérico igual
        setInfo({});
      } finally {
        setLoading(false);
      }
    })();
  }, [ref, info]);

  // Confetti-ish micro entrance animation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isEs = lang !== "en";
  const t = (es, en) => (isEs ? es : en);

  const eventTitle = info?.event_title || "";
  const distance = info?.distance_label || "";
  const fullName = info?.full_name || "";
  const email = info?.email || "";
  const eventId = info?.event_id;
  const isPaid = (info?.final_price || 0) > 0;

  // Compartir la inscripción — texto motivador con URL del evento.
  const buildShareData = () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = eventId ? `${origin}/eventos/${eventId}` : origin;
    const distTxt = distance ? ` · ${distance}` : "";
    const evTxt = eventTitle ? ` ${eventTitle}` : "";
    const text = isEs
      ? `¡Me inscribí en${evTxt}${distTxt}! 🏔️ Nos vemos en la línea de partida. Corre conmigo:`
      : `I just registered for${evTxt}${distTxt}! 🏔️ See you at the starting line. Run with me:`;
    return { title: eventTitle || "Pro Vertical", text, url };
  };

  const handleShare = async () => {
    const share = buildShareData();
    // Native share sheet (iOS/Android → WhatsApp, Instagram Stories, etc.)
    if (navigator.share) {
      try {
        await navigator.share(share);
      } catch (err) {
        // El usuario canceló — no mostrar error.
      }
      return;
    }
    // Fallback: copiar al portapapeles
    try {
      await navigator.clipboard.writeText(`${share.text} ${share.url}`);
      toast.success(t("Enlace copiado al portapapeles", "Link copied to clipboard"));
    } catch {
      toast.error(t("No se pudo compartir", "Could not share"));
    }
  };

  const handleShareWhatsApp = () => {
    const share = buildShareData();
    const msg = encodeURIComponent(`${share.text} ${share.url}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank", "noopener,noreferrer");
  };

  return (
    <main className="pt-20 min-h-screen bg-[#050505] flex items-center justify-center px-4 py-10" data-testid="registration-success-page">
      <div className="w-full max-w-2xl">
        <div className="relative bg-gradient-to-b from-[#0F1F13] to-[#0A0A0A] border border-green-500/40 shadow-2xl shadow-green-500/10 p-8 sm:p-12 text-center overflow-hidden">
          {/* Decorative badge glow */}
          <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-green-500/10 blur-3xl pointer-events-none" />

          {/* Icon */}
          <div className="relative inline-flex items-center justify-center mb-6">
            <div className="absolute inset-0 rounded-full bg-green-500/20 blur-xl animate-pulse" />
            <CheckCircle size={88} weight="fill" className="relative text-green-400" data-testid="registration-success-icon" />
          </div>

          {/* Title */}
          <h1 className="relative text-3xl sm:text-4xl font-black uppercase tracking-tight font-['Cabinet_Grotesk'] mb-3 text-white" data-testid="registration-success-title">
            {t("¡Inscripción confirmada!", "Registration confirmed!")}
          </h1>
          <p className="text-gray-300 text-base sm:text-lg mb-8 max-w-lg mx-auto">
            {loading
              ? t("Confirmando tu inscripción...", "Confirming your registration...")
              : isPaid
              ? t("Tu pago fue procesado correctamente. Ya estás dentro.", "Your payment was processed. You're in.")
              : t("Ya estás inscrito. Nos vemos en la línea de partida.", "You're registered. See you at the starting line.")}
          </p>

          {/* Details card */}
          {(eventTitle || distance || fullName || email) && (
            <div className="bg-black/40 border border-white/10 p-5 sm:p-6 text-left mb-8 space-y-3" data-testid="registration-success-details">
              {eventTitle && (
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#FF4D00] mt-1 shrink-0" />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">{t("Evento", "Event")}</div>
                    <div className="text-white font-semibold" data-testid="success-event-title">{eventTitle}</div>
                    {distance && (
                      <div className="inline-block mt-1 px-2 py-0.5 text-xs bg-[#FF4D00]/20 text-[#FF4D00] font-bold" data-testid="success-distance">
                        {distance}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {fullName && (
                <div className="flex items-start gap-3 pt-2 border-t border-white/5">
                  <CheckCircle size={18} className="text-green-400 mt-1 shrink-0" />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">{t("Participante", "Participant")}</div>
                    <div className="text-white font-semibold" data-testid="success-full-name">{fullName}</div>
                  </div>
                </div>
              )}
              {email && (
                <div className="flex items-start gap-3 pt-2 border-t border-white/5">
                  <Envelope size={18} className="text-blue-400 mt-1 shrink-0" />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">{t("Confirmación enviada a", "Confirmation sent to")}</div>
                    <div className="text-white text-sm break-all" data-testid="success-email">{email}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-gray-500 mb-6">
            {t(
              "Guarda esta pantalla o revisa tu correo. Si tienes dudas, escríbenos por WhatsApp.",
              "Save this screen or check your email. If you have any doubts, message us on WhatsApp."
            )}
          </p>

          {/* Share block */}
          <div className="mb-6 pt-4 border-t border-white/5">
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">
              {t("Corre acompañado — invita a alguien", "Run together — invite someone")}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={handleShare}
                className="bg-white text-black hover:bg-gray-200 font-bold h-11 px-5"
                data-testid="success-share-btn"
              >
                <ShareNetwork size={16} weight="bold" className="mr-2" />
                {t("Compartir mi inscripción", "Share my registration")}
              </Button>
              <Button
                onClick={handleShareWhatsApp}
                variant="outline"
                className="border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 h-11 px-5"
                data-testid="success-share-whatsapp-btn"
              >
                <WhatsappLogo size={16} weight="fill" className="mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {eventId ? (
              <Button
                onClick={() => navigate(`/eventos/${eventId}`)}
                className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold h-12 px-6"
                data-testid="success-back-to-event-btn"
              >
                <ArrowLeft size={16} className="mr-2" />
                {t("Volver al evento", "Back to event")}
              </Button>
            ) : (
              <Link to="/eventos">
                <Button className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold h-12 px-6 w-full" data-testid="success-back-to-events-btn">
                  <ArrowLeft size={16} className="mr-2" />
                  {t("Ver más eventos", "See more events")}
                </Button>
              </Link>
            )}
            <Link to="/">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 h-12 px-6 w-full" data-testid="success-home-btn">
                <House size={16} className="mr-2" />
                {t("Ir al inicio", "Go home")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
